import React, { useState } from 'react';
import { ArrowLeft, Copy, Check, Keyboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const EMOJI_GROUPS = [
  {
    title: "Most Popular",
    emojis: ["❤️", "😊", "🥰", "😂", "👍", "🎉", "✨", "🔥", "💯", "🙌"]
  },
  {
    title: "Love & Hearts",
    emojis: ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔"]
  },
  {
    title: "Smileys",
    emojis: ["😀", "😃", "😄", "😁", "😅", "😂", "🤣", "☺️", "😊", "😇"]
  },
  {
    title: "Gestures",
    emojis: ["👍", "👎", "👌", "✌️", "🤞", "🤝", "🙏", "👏", "🙌", "👐"]
  }
];

const CopyPasteEmoji = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const handleCopy = (emoji: string) => {
    navigator.clipboard.writeText(emoji);
    setCopied(emoji);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <>
      <Helmet>
        <title>🔥 Copy and Paste Emojis [{currentYear}] | #1 Emoji Collection ✨</title>
        <meta name="description" content={`【INSTANT COPY】 2000+ emojis to copy and paste! 🚀 One-click copy 📱 Mobile-friendly ⚡ Updated for ${currentYear} ✨ Works everywhere! Most popular emojis for Instagram, WhatsApp & more!`} />
        <link rel="canonical" href="https://emojicollection.net/copy-paste-emoji" />
        
        <meta property="og:title" content={`Copy and Paste Emojis [${currentYear}] | #1 Emoji Collection`} />
        <meta property="og:description" content={`2000+ emojis to copy and paste! Updated for ${currentYear} with one-click copy functionality. Works on all platforms and devices!`} />
        <meta property="og:url" content="https://emojicollection.net/copy-paste-emoji" />
        <meta property="og:type" content="website" />
        
        <meta name="keywords" content="copy and paste emoji, emoji copy paste, copy paste emojis, emoji keyboard, emoji collection, emojis to copy, copy emoji" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Copy and Paste Emojis",
            "applicationCategory": "UtilityApplication",
            "operatingSystem": "Any",
            "description": `The #1 emoji copy and paste tool. Updated for ${currentYear} with 2000+ emojis and instant copy functionality.`,
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white mb-6">
                <Keyboard className="w-8 h-8" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Copy and Paste Emojis [{currentYear}] 🔥
              </h1>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                The fastest way to copy and paste emojis! 2000+ emojis organized by category. One-click copy, mobile-friendly, and works everywhere. Updated for {currentYear} with the latest emojis.
              </p>

              {EMOJI_GROUPS.map((group, groupIndex) => (
                <div key={groupIndex} className="mb-12">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                    {group.title}
                  </h2>
                  <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-4">
                    {group.emojis.map((emoji, emojiIndex) => (
                      <button
                        key={emojiIndex}
                        onClick={() => handleCopy(emoji)}
                        className="relative group p-4 text-3xl bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <span className="block mb-2">{emoji}</span>
                        <span className={`absolute inset-0 flex items-center justify-center bg-black/50 text-white text-sm font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity ${
                          copied === emoji ? 'opacity-100 bg-green-500/50' : ''
                        }`}>
                          {copied === emoji ? (
                            <>
                              <Check className="mr-1" size={16} />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="mr-1" size={16} />
                              Copy
                            </>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <section className="mt-12 border-t border-gray-200 pt-8">
                <h2 className="text-2xl font-semibold mb-6">Features</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 text-purple-500 mb-4">
                      <Copy className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">One-Click Copy</h3>
                    <p className="text-gray-600">Instantly copy any emoji with a single click</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-pink-100 text-pink-500 mb-4">
                      <Keyboard className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">2000+ Emojis</h3>
                    <p className="text-gray-600">Huge collection of emojis to choose from</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-500 mb-4">
                      <Check className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">Works Everywhere</h3>
                    <p className="text-gray-600">Compatible with all platforms and devices</p>
                  </div>
                </div>
              </section>

              <section className="mt-12 border-t border-gray-200 pt-8">
                <h2 className="text-2xl font-semibold mb-6">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  <details className="group bg-gray-50 rounded-lg">
                    <summary className="cursor-pointer p-4 font-medium">How do I copy emojis?</summary>
                    <div className="p-4 pt-0 text-gray-600">
                      Simply click any emoji to copy it instantly to your clipboard. You can then paste it anywhere using Ctrl+V (Windows) or Cmd+V (Mac).
                    </div>
                  </details>
                  <details className="group bg-gray-50 rounded-lg">
                    <summary className="cursor-pointer p-4 font-medium">Where can I use these emojis?</summary>
                    <div className="p-4 pt-0 text-gray-600">
                      Our emojis work everywhere - social media (Instagram, Facebook, Twitter), messaging apps (WhatsApp, Messenger), emails, documents, and more!
                    </div>
                  </details>
                  <details className="group bg-gray-50 rounded-lg">
                    <summary className="cursor-pointer p-4 font-medium">Are these emojis free?</summary>
                    <div className="p-4 pt-0 text-gray-600">
                      Yes! All emojis are completely free to use. No registration or subscription required.
                    </div>
                  </details>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CopyPasteEmoji;